import React, { useState, useMemo } from 'react';
import { 
  TrendingUp, DollarSign, Target, PieChart, BarChart3, 
  ArrowUp, ArrowDown, Plus, Trash2, Pencil, X, TrendingDown,
  Megaphone, Wrench, BookOpen, Cpu, HelpCircle, ChevronLeft, ChevronRight,
  Info, AlertTriangle, CheckCircle, Zap
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, 
  PieChart as RechartPie, Pie, Cell, Legend
} from 'recharts';
import { formatCurrency } from '@/utils/helpers';
import useApiData from '@/hooks/useApiData';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/utils/api';

const COLORS = ['#6366f1', '#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ec4899'];

const CATEGORIAS = ['Marketing', 'Publicidade', 'Ferramentas', 'Capacitação', 'Tecnologia', 'Outros'];
const CANAIS = ['Google Ads', 'Meta Ads', 'LinkedIn Ads', 'TikTok Ads', 'SEO', 'Influencer', 'E-mail Marketing', 'Eventos', 'Indicações', 'Outros'];

const ICON_CATEGORIA = {
  'Marketing': Megaphone,
  'Publicidade': TrendingUp,
  'Ferramentas': Wrench,
  'Capacitação': BookOpen,
  'Tecnologia': Cpu,
  'Outros': HelpCircle,
};

// ─── Helpers ───
function getMesAtual() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function formatMes(mes) {
  if (!mes) return '';
  const [y, m] = mes.split('-');
  const meses = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  return `${meses[parseInt(m) - 1]}/${y}`;
}

function navigateMes(mes, delta) {
  if (!mes) return getMesAtual();
  const [y, m] = mes.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// ─── Modal de Investimento ───
function InvestimentoModal({ open, onClose, onSave, editData }) {
  const [form, setForm] = useState({
    descricao: '', categoria: 'Marketing', canal: 'Google Ads',
    valor: '', retorno_obtido: '', mes_referencia: getMesAtual(), observacoes: ''
  });

  React.useEffect(() => {
    if (editData) {
      setForm({
        descricao: editData.descricao || '',
        categoria: editData.categoria || 'Marketing',
        canal: editData.canal || 'Google Ads',
        valor: editData.valor || '',
        retorno_obtido: editData.retorno_obtido || '',
        mes_referencia: editData.mes_referencia || getMesAtual(),
        observacoes: editData.observacoes || ''
      });
    } else {
      setForm({ descricao: '', categoria: 'Marketing', canal: 'Google Ads', valor: '', retorno_obtido: '', mes_referencia: getMesAtual(), observacoes: '' });
    }
  }, [editData, open]);

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      valor: parseFloat(form.valor) || 0,
      retorno_obtido: form.retorno_obtido ? parseFloat(form.retorno_obtido) : null
    });
  };

  const CatIcon = ICON_CATEGORIA[form.categoria] || HelpCircle;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-100 overflow-hidden scale-in">
        <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-gradient-to-r from-violet-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-violet-100 flex items-center justify-center">
              <CatIcon className="h-5 w-5 text-violet-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">{editData ? 'Editar Investimento' : 'Novo Investimento'}</h2>
              <p className="text-xs text-slate-500">Registre custos e retornos vinculados aos seus canais</p>
            </div>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-1 block">Descrição *</label>
            <input
              required
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              placeholder="Ex: Campanha Google Ads - Abril"
              value={form.descricao}
              onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-1 block">Categoria *</label>
              <select
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                value={form.categoria}
                onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))}
              >
                {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-1 block">Canal (Origem) *</label>
              <select
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                value={form.canal}
                onChange={e => setForm(f => ({ ...f, canal: e.target.value }))}
              >
                {CANAIS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-1 block">Valor Investido (R$) *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-bold">R$</span>
                <input
                  required type="number" step="0.01" min="0"
                  className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                  placeholder="0,00"
                  value={form.valor}
                  onChange={e => setForm(f => ({ ...f, valor: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-1 block flex items-center justify-between">
                <span>Retorno Obtido (R$)</span>
                <span className="text-slate-400 font-normal text-[10px]">(opcional)</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-bold">R$</span>
                <input
                  type="number" step="0.01" min="0"
                  className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                  placeholder="0,00"
                  value={form.retorno_obtido}
                  onChange={e => setForm(f => ({ ...f, retorno_obtido: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-1 block">Mês de Referência *</label>
            <input
              required type="month"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              value={form.mes_referencia}
              onChange={e => setForm(f => ({ ...f, mes_referencia: e.target.value }))}
            />
          </div>

          <div>
            <label className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-1 block">Observações</label>
            <textarea
              rows={2}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
              placeholder="Detalhes ou campanha específica..."
              value={form.observacoes}
              onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Cancelar</Button>
            <Button type="submit" className="flex-1 bg-violet-600 hover:bg-violet-700 text-white">
              {editData ? 'Salvar Alterações' : 'Adicionar Investimento'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Card Simples de KPIs ───
function KpiCard({ title, value, sub, icon: Icon, color = 'bg-slate-100 text-slate-600', border = 'border-slate-100' }) {
  return (
    <div className={`bg-white p-5 rounded-2xl border ${border} shadow-sm`}>
      <div className="flex items-center justify-between mb-3">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">{title}</p>
      <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

// ─── Página Principal de ROI ───
export default function ROIInvestimentos() {
  const [mesFiltro, setMesFiltro] = useState(getMesAtual());
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const { data: investimentosData, isLoading: loadingInv, refresh: refreshInv } = useApiData(`/advanced/investimentos?mes=${mesFiltro}`);
  // eslint-disable-next-line
  const investimentos = Array.isArray(investimentosData) ? investimentosData : [];

  const handleSave = async (data) => {
    try {
      if (editData?.id) {
        await api.put(`/advanced/investimentos/${editData.id}`, data);
        toast.success("Investimento atualizado!");
      } else {
        await api.post(`/advanced/investimentos`, data);
        toast.success("Investimento criado!");
      }
      refreshInv();
      setModalOpen(false);
      setEditData(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar investimento');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover este investimento permanentemente?')) return;
    try {
      await api.delete(`/advanced/investimentos/${id}`);
      toast.success("Investimento apagado.");
      refreshInv();
    } catch {
      toast.error('Erro ao apagar');
    }
  };

  // Cálculos de KPI
  const totalInvestido = investimentos.reduce((s, i) => s + (i.valor || 0), 0);
  const totalRetorno = investimentos.reduce((s, i) => s + (i.retorno_obtido || 0), 0);
  const lucroReal = totalRetorno - totalInvestido;
  const roiTotal = totalInvestido > 0 ? ((lucroReal) / totalInvestido) * 100 : 0;

  // Group por canal para gráfico de Pizza e Tabela de Canal
  const groupedByChannel = useMemo(() => {
    const map = {};
    investimentos.forEach(inv => {
      if (!map[inv.canal]) map[inv.canal] = { canal: inv.canal, investido: 0, retorno: 0 };
      map[inv.canal].investido += inv.valor || 0;
      map[inv.canal].retorno += inv.retorno_obtido || 0;
    });
    return Object.values(map)
      .map(c => ({
        ...c,
        roi: c.investido > 0 ? ((c.retorno - c.investido) / c.investido) * 100 : 0
      }))
      .sort((a,b) => b.roi - a.roi); // ordena pelo melhor ROI
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investimentosData]);

  // Geração de Insights automáticos
  const insights = [];
  if (investimentos.length > 0) {
    if (roiTotal > 0) {
      insights.push({ icon: TrendingUp, msg: `Seu ROI no período foi positivo (${roiTotal.toFixed(1)}%). O investimento se pagou.`, color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' });
    } else if (roiTotal < 0) {
      insights.push({ icon: TrendingDown, msg: `Cuidado: o ROI geral está negativo (${roiTotal.toFixed(1)}%). Avalie a eficiência dos canais.`, color: 'text-red-700', bg: 'bg-red-50 border-red-200' });
    }

    if (groupedByChannel.length > 0) {
      const bestChannel = groupedByChannel[0];
      if (bestChannel.retorno > bestChannel.investido) {
         insights.push({ icon: Zap, msg: `A origem "${bestChannel.canal}" teve o melhor desempenho com ROI de ${bestChannel.roi.toFixed(1)}%.`, color: 'text-violet-700', bg: 'bg-violet-50 border-violet-200' });
      }
    }
  } else {
    insights.push({ icon: Info, msg: 'Sem dados suficientes para gerar insights. Comece cadastrando seus investimentos de marketing.', color: 'text-slate-700', bg: 'bg-slate-50 border-slate-200' });
  }

  return (
    <div className="space-y-6 pt-4 lg:pt-0" data-testid="roi-page">
      {/* Header com Navegação de Mês */}
      <div className="flex items-center justify-between flex-wrap gap-4 mt-2">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight text-slate-900">ROI & Investimentos</h1>
          <p className="text-slate-500 mt-1">Controle estratégico de gastos com marketing, ferramentas e captação.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl px-2 py-1 shadow-sm">
            <button onClick={() => setMesFiltro(m => navigateMes(m, -1))} className="p-1.5 hover:text-violet-600 rounded">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-bold text-slate-700 px-3 min-w-[90px] text-center">{formatMes(mesFiltro)}</span>
            <button onClick={() => setMesFiltro(m => navigateMes(m, 1))} className="p-1.5 hover:text-violet-600 rounded">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <Button
            className="bg-violet-600 hover:bg-violet-700 text-white gap-2 rounded-xl shadow-sm"
            onClick={() => { setEditData(null); setModalOpen(true); }}
          >
            <Plus className="h-4 w-4" /> Nova Despesa
          </Button>
        </div>
      </div>

      {/* Alertas / Insights */}
      {insights.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {insights.map((ins, i) => (
            <div key={i} className={`flex items-start gap-3 p-4 rounded-xl border ${ins.bg} ${ins.color} shadow-sm`}>
              <ins.icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm leading-relaxed font-medium">{ins.msg}</p>
            </div>
          ))}
        </div>
      )}

      {/* Resumo Overview KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard 
          title="Total Investido" 
          value={formatCurrency(totalInvestido)} 
          icon={DollarSign} 
          color="bg-slate-100 text-slate-600" 
        />
        <KpiCard 
          title="Receita Gerada" 
          value={formatCurrency(totalRetorno)} 
          icon={ArrowUp} 
          color="bg-blue-100 text-blue-600" 
        />
        <KpiCard 
          title="Lucro" 
          value={formatCurrency(lucroReal)} 
          icon={TrendingUp} 
          color={lucroReal >= 0 ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'} 
        />
        <div className={`bg-white p-5 rounded-2xl border-2 shadow-sm ${
            roiTotal >= 100 ? 'border-emerald-200 bg-emerald-50/20' :
            roiTotal > 0 ? 'border-blue-200 bg-blue-50/20' :
            roiTotal < 0 ? 'border-red-200 bg-red-50/20' : 'border-slate-100'
        }`}>
          <div className="flex items-center justify-between mb-3">
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${
              roiTotal >= 100 ? 'bg-emerald-100 text-emerald-600' :
              roiTotal > 0 ? 'bg-blue-100 text-blue-600' :
              roiTotal < 0 ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500'
            }`}>
              <Target className="h-5 w-5" />
            </div>
            {investimentos.length > 0 && (
              <span className={`text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-widest ${
                roiTotal >= 100 ? 'bg-emerald-100 text-emerald-700' :
                roiTotal > 0 ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'
              }`}>
                {roiTotal >= 100 ? 'Excelente' : roiTotal > 0 ? 'Positivo' : 'Negativo'}
              </span>
            )}
          </div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">ROI Médio (Período)</p>
          <p className={`text-3xl font-bold mt-1 ${
             roiTotal >= 0 ? 'text-slate-900' : 'text-red-600'
          }`}>{investimentos.length > 0 ? `${roiTotal.toFixed(1)}%` : '--'}</p>
        </div>
      </div>

      {/* Charts & Tabela Origem */}
      {investimentos.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Gráfico Pizza */}
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Distribuição de Investimentos (Por Canal)</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RechartPie>
                  <Pie
                    data={groupedByChannel} cx="50%" cy="50%" innerRadius={60} outerRadius={90}
                    paddingAngle={3} dataKey="investido" nameKey="canal"
                  >
                    {groupedByChannel.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={v => formatCurrency(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Legend />
                </RechartPie>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Gráfico Comparativo Retorno x Investimento */}
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
             <h3 className="text-sm font-bold text-slate-800 mb-4">Investimento vs Retorno por Canal</h3>
             <div className="h-64">
               <ResponsiveContainer width="100%" height="100%">
                 <BarChart data={groupedByChannel} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="canal" tick={{ fontSize: 11, fill: '#64748b' }} />
                    <YAxis tickFormatter={v => `R$${v/1000}k`} tick={{ fontSize: 11, fill: '#64748b' }} />
                    <RechartsTooltip formatter={v => formatCurrency(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} wrapperStyle={{ zIndex: 100 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="investido" name="Inves. (R$)" fill="#cbd5e1" radius={[4,4,0,0]} barSize={30} />
                    <Bar dataKey="retorno" name="Retorno (R$)" fill="#6366f1" radius={[4,4,0,0]} barSize={30} />
                 </BarChart>
               </ResponsiveContainer>
             </div>
          </div>
        </div>
      ) : null}

      {/* Tabela de Lançamentos */}
      <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-slate-800">Lançamentos Detalhados</h3>
        </div>

        {loadingInv ? (
          <div className="py-12 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" /></div>
        ) : investimentos.length === 0 ? (
          <div className="py-12 flex flex-col items-center justify-center text-center">
            <div className="h-16 w-16 rounded-2xl bg-slate-50 flex items-center justify-center mb-4">
              <BookOpen className="h-8 w-8 text-slate-300" />
            </div>
            <p className="text-slate-800 font-bold text-lg mb-1">Nenhum investimento registrado</p>
            <p className="text-slate-500 text-sm max-w-sm mb-4">Cadastre campanhas do Facebook Ads, contratação de ferramentas de marketing ou SEO para gerenciar seu orçamento.</p>
            <Button onClick={() => { setEditData(null); setModalOpen(true); }} className="bg-violet-600 hover:bg-violet-700">Adicionar Agora</Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-100 text-xs uppercase tracking-wider text-slate-400">
                  <th className="py-3 px-4 font-semibold">Descrição</th>
                  <th className="py-3 px-4 font-semibold">Categoria / Origem</th>
                  <th className="py-3 px-4 font-semibold text-right">Investido</th>
                  <th className="py-3 px-4 font-semibold text-right">Retornado</th>
                  <th className="py-3 px-4 font-semibold text-right">ROI</th>
                  <th className="py-3 px-4 font-semibold text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {investimentos.map(inv => {
                  const CatIcon = ICON_CATEGORIA[inv.categoria] || HelpCircle;
                  const itemRoi = inv.investido > 0 ? ((inv.retorno_obtido - inv.valor) / inv.valor) * 100 : (inv.retorno_obtido > 0 && inv.valor > 0 ? ((inv.retorno_obtido - inv.valor) / inv.valor) * 100 : null);
                  const _roi = inv.valor && inv.retorno_obtido ? ((inv.retorno_obtido - inv.valor) / inv.valor) * 100 : null;
                  
                  return (
                    <tr key={inv.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors group">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-lg bg-slate-100 flex justify-center items-center">
                            <CatIcon className="h-4 w-4 text-slate-500" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{inv.descricao}</p>
                            {inv.observacoes && <p className="text-[10px] text-slate-400 truncate max-w-[200px]">{inv.observacoes}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-xs text-slate-600 font-medium">{inv.categoria}</p>
                        <p className="text-[10px] font-bold text-violet-600">{inv.canal}</p>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <p className="text-sm font-bold text-slate-900">{formatCurrency(inv.valor)}</p>
                      </td>
                      <td className="py-3 px-4 text-right">
                        {inv.retorno_obtido > 0 ? (
                          <p className="text-sm font-bold text-emerald-600">{formatCurrency(inv.retorno_obtido)}</p>
                        ) : (
                          <span className="text-xs text-slate-400 italic">--</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                         {_roi !== null ? (
                          <div className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${_roi >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                            {_roi > 0 ? '+' : ''}{_roi.toFixed(0)}%
                          </div>
                         ) : (
                           <span className="text-xs text-slate-300">--</span>
                         )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => { setEditData(inv); setModalOpen(true); }} className="p-1.5 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg">
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button onClick={() => handleDelete(inv.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <InvestimentoModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditData(null); }}
        onSave={handleSave}
        editData={editData}
      />
    </div>
  );
}
